/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useCallback, useState } from 'react'

import { isPast } from 'date-fns'
import { jwtDecode } from 'jwt-decode'

import { useRouter } from '../../../../index'
import { refreshToken } from '../auth.service'

type OwnProps = {
  redirectUrl?: string
}

export let ACCESSTOKEN = ''

export class TokenManipulate {
  static print(accessToken: string, refreshToken: string) {
    ACCESSTOKEN = accessToken
    localStorage.setItem('token', refreshToken)
    localStorage.setItem('iat', String(Math.floor(Date.now() / 1000)))
  }

  static async refresh(token: string) {
    if (!token) return new Promise((_resolve, reject) => reject())

    return await refreshToken(token).then(tokens => {
      this.print(tokens.jwtToken, tokens.refreshToken)
    })
  }

  static remove() {
    ACCESSTOKEN = ''
    localStorage.removeItem('token')
    localStorage.removeItem('iat')
  }
}

export const useToken = ({ redirectUrl }: OwnProps) => {
  const { location, navigate } = useRouter()

  const check = async () => {
    const { from } = (location.state as any) || {
      from: { pathname: redirectUrl },
    }

    redirectUrl && navigate(from)
  }

  return {
    check,
  }
}

export const checkToken = async (config: any, isXAuth?: boolean) => {
  if (config.url === `/tokens/jwt/refresh`) return config

  const refreshToken = async () => {
    return await TokenManipulate.refresh(
      localStorage.getItem('token') as string,
    )
      .then(() => {
        if (isXAuth) {
          config.headers['X-Auth'] = `${ACCESSTOKEN}`
          return config
        }
        config.headers.Authorization = `Bearer ${ACCESSTOKEN}`

        return config
      })
      .finally(() => config)
  }

  if (
    config.headers?.Authorization &&
    config.headers?.Authorization.toString() === 'Bearer '
  ) {
    return await refreshToken()
  }

  if (ACCESSTOKEN) {
    const tokenBody = jwtDecode(ACCESSTOKEN)

    // Считаем токен "просроченным" за 15 минут до истечения (exp - 15 минут)
    // Т.е. если до истечения осталось менее 15 минут, обновляем.
    if (tokenBody.exp && isPast(tokenBody.exp * 1000 - 15 * 60 * 1000)) {
      return await refreshToken()
    }

    if (isXAuth) {
      config.headers['X-Auth'] = `${ACCESSTOKEN}`
      return config
    }

    config.headers.Authorization = `Bearer ${ACCESSTOKEN}`
    return config
  }

  return config
}

export const useUpdateAccessToken = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const updateToken = useCallback(async () => {
    setIsLoading(true)
    if (
      localStorage.getItem('iat') &&
      Number(localStorage.getItem('iat')) + 60 * 45 <
        Math.floor(Date.now() / 1000)
    ) {
      localStorage.removeItem('iat')
      await TokenManipulate.refresh(
        localStorage.getItem('token') as string,
      ).catch(() => setIsError(true))
    } else if (!ACCESSTOKEN) {
      await TokenManipulate.refresh(
        localStorage.getItem('token') as string,
      ).catch(() => setIsError(true))
    }
    setIsLoading(false)
  }, [setIsLoading, setIsError])

  return { isLoading, isError, updateToken }
}
